/**
 * Treatment service for managing therapy treatments
 */
import StorageService from './StorageService';
import Treatment from '../models/Treatment';

class TreatmentService {
  /**
   * Constructor
   */
  constructor() {
    this.storageService = new StorageService('treatments');
  }

  /**
   * Get all treatments
   * @returns {Array} All treatments
   */
  getAllTreatments() {
    return this.storageService.getAllAsArray().map(treatment => 
      Treatment.fromJSON(treatment)
    );
  }

  /**
   * Get active treatments
   * @returns {Array} All active treatments
   */
  getActiveTreatments() {
    return this.storageService.find(treatment => treatment.isActive)
      .map(treatment => Treatment.fromJSON(treatment));
  }

  /**
   * Get treatment by ID
   * @param {string} id - Treatment ID
   * @returns {Treatment|null} Treatment or null if not found
   */
  getTreatmentById(id) {
    const treatmentData = this.storageService.getById(id);
    if (!treatmentData) return null;
    
    return Treatment.fromJSON(treatmentData);
  }

  /**
   * Create treatment
   * @param {Object} treatmentData - Treatment data
   * @returns {Treatment} Created treatment
   */
  createTreatment(treatmentData) {
    const treatment = new Treatment(treatmentData);
    this.storageService.save(treatment.id, treatment.toJSON());
    return treatment;
  }

  /**
   * Update treatment
   * @param {string} id - Treatment ID
   * @param {Object} treatmentData - Treatment data to update
   * @returns {Treatment|null} Updated treatment or null if not found
   */
  updateTreatment(id, treatmentData) {
    const existingTreatmentData = this.storageService.getById(id);
    if (!existingTreatmentData) return null;
    
    const treatment = Treatment.fromJSON(existingTreatmentData);
    treatment.update(treatmentData);
    
    this.storageService.save(treatment.id, treatment.toJSON());
    return treatment;
  }

  /**
   * Delete treatment
   * @param {string} id - Treatment ID
   * @returns {boolean} Whether deletion was successful
   */
  deleteTreatment(id) {
    return this.storageService.delete(id);
  }

  /**
   * Search treatments by text query
   * @param {string} query - Search query
   * @returns {Array} Matching treatments
   */
  searchTreatments(query) {
    if (!query) return this.getAllTreatments();
    
    const lowercasedQuery = query.toLowerCase();
    
    return this.storageService.find(treatment => {
      // Search in various fields
      return (
        treatment.name?.toLowerCase().includes(lowercasedQuery) ||
        treatment.description?.toLowerCase().includes(lowercasedQuery) ||
        treatment.type?.toLowerCase().includes(lowercasedQuery) ||
        (treatment.tags && treatment.tags.some(tag => 
          tag.toLowerCase().includes(lowercasedQuery)
        ))
      );
    }).map(treatment => Treatment.fromJSON(treatment));
  }

  /**
   * Filter treatments by type
   * @param {string} type - Treatment type
   * @returns {Array} Filtered treatments
   */
  filterByType(type) {
    if (!type) return this.getAllTreatments();
    
    return this.storageService.find(treatment => 
      treatment.type === type
    ).map(treatment => Treatment.fromJSON(treatment));
  }

  /**
   * Filter treatments by practitioner
   * @param {string} practitionerId - Practitioner ID
   * @returns {Array} Treatments that can be performed by the practitioner
   */
  filterByPractitioner(practitionerId) {
    if (!practitionerId) return this.getAllTreatments();
    
    return this.storageService.find(treatment => 
      treatment.practitioners && treatment.practitioners.includes(practitionerId)
    ).map(treatment => Treatment.fromJSON(treatment));
  }

  /**
   * Filter treatments by applicable condition
   * @param {string} condition - Medical condition
   * @returns {Array} Treatments applicable for the condition
   */
  filterByCondition(condition) {
    if (!condition) return this.getAllTreatments();
    
    const lowercasedCondition = condition.toLowerCase();
    
    return this.storageService.find(treatment => 
      treatment.applicableConditions && treatment.applicableConditions.some(c => 
        c.toLowerCase().includes(lowercasedCondition)
      )
    ).map(treatment => Treatment.fromJSON(treatment));
  }

  /**
   * Sort treatments by price
   * @param {boolean} [ascending=true] - Sort direction
   * @returns {Array} Sorted treatments
   */
  sortByPrice(ascending = true) {
    const treatments = this.getAllTreatments();
    
    return treatments.sort((a, b) => {
      return ascending ? a.price - b.price : b.price - a.price;
    });
  }

  /**
   * Sort treatments by duration
   * @param {boolean} [ascending=true] - Sort direction
   * @returns {Array} Sorted treatments
   */
  sortByDuration(ascending = true) {
    const treatments = this.getAllTreatments();
    
    return treatments.sort((a, b) => {
      return ascending ? a.duration - b.duration : b.duration - a.duration;
    });
  }

  /**
   * Get all unique treatment types
   * @returns {Array} All unique treatment types
   */
  getAllTreatmentTypes() {
    const typesSet = new Set();
    
    this.getAllTreatments().forEach(treatment => {
      if (treatment.type) {
        typesSet.add(treatment.type);
      }
    });
    
    return Array.from(typesSet).sort();
  }

  /**
   * Get all unique tags from treatments
   * @returns {Array} All unique tags
   */
  getAllTags() {
    const tagsSet = new Set();
    
    this.getAllTreatments().forEach(treatment => {
      if (treatment.tags && Array.isArray(treatment.tags)) {
        treatment.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    
    return Array.from(tagsSet).sort();
  }

  /**
   * Add a practitioner who can perform a treatment
   * @param {string} treatmentId - Treatment ID
   * @param {string} practitionerId - Practitioner ID
   * @returns {Treatment|null} Updated treatment or null if not found
   */
  addPractitionerToTreatment(treatmentId, practitionerId) {
    const treatment = this.getTreatmentById(treatmentId);
    if (!treatment) return null;
    
    if (treatment.addPractitioner(practitionerId)) {
      this.storageService.save(treatment.id, treatment.toJSON());
    }
    
    return treatment;
  }

  /**
   * Remove a practitioner from a treatment
   * @param {string} treatmentId - Treatment ID
   * @param {string} practitionerId - Practitioner ID
   * @returns {Treatment|null} Updated treatment or null if not found
   */
  removePractitionerFromTreatment(treatmentId, practitionerId) {
    const treatment = this.getTreatmentById(treatmentId);
    if (!treatment) return null;
    
    if (treatment.removePractitioner(practitionerId)) {
      this.storageService.save(treatment.id, treatment.toJSON());
    }
    
    return treatment;
  }

  /**
   * Get total number of treatments
   * @returns {number} Treatment count
   */
  getTreatmentCount() {
    return this.storageService.count();
  }

  /**
   * Import treatments from JSON
   * @param {string} jsonData - JSON string of treatment data
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importTreatments(jsonData, merge = false) {
    return this.storageService.importData(jsonData, merge);
  }

  /**
   * Export treatments as JSON
   * @returns {string} JSON string of treatment data
   */
  exportTreatments() {
    return this.storageService.exportData();
  }
}

// Create singleton instance
const treatmentService = new TreatmentService();

export default treatmentService;